import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tabs } from "../../../components/ui";
import { DocumentChecklist } from "../../../features/shipmentDocuments/DocumentChecklist";
import { DOCS_NS } from "../../../features/shipmentDocuments/meta";
import CrudSection from "../components/CrudSection";
import DataTable from "../components/DataTable";
import FeatureDashboard from "../../../components/base/FeatureDashboard";
import MultimodalQuoter from "../components/MultimodalQuoter";
import Modal from "../../../components/base/Modal";
import { StatusTextBadge } from "../../../components/base/StatusTextBadge";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { formatMoney } from "../lib/money";
import { useBookings } from "../services/bookingService";
import type { BookingStatus, CargoBooking, PaymentStatus } from "../booking/bookingTypes";
import type { CrudColumn, CrudField, CrudRow } from "../types";

// ===========================================================================
// Módulo A — Carga aérea y marítima (cotizador + disponibilidad + compra).
// Front-end scaffold: la disponibilidad/tarifas reales y el booking llegarán de
// la API del proveedor; aquí van datos de ejemplo + el cotizador interactivo.
// ===========================================================================

export function AereaMaritimaDashboard() {
  return (
    <FeatureDashboard noticeKey="cargoMarket.mockNotice"
      title="Carga aérea y marítima"
      subtitle="Cotización y compra de espacio de carga vía proveedor"
      icon="ri-plane-line"
      stats={[
        { label: "Cotizaciones (mes)", value: "342", icon: "ri-calculator-line", change: "+12%", positive: true },
        { label: "Reservas confirmadas", value: "58", icon: "ri-flight-takeoff-line", change: "+6", positive: true },
        { label: "Rutas activas", value: "27", icon: "ri-route-line" },
        { label: "Ahorro promedio", value: "14%", icon: "ri-money-dollar-circle-line", change: "+2pp", positive: true },
      ]}
      chart={{
        title: "Cotizaciones por modo (mes)",
        data: [
          { name: "Aérea", value: 180 }, { name: "Marítima FCL", value: 95 },
          { name: "Marítima LCL", value: 42 }, { name: "Terrestre int.", value: 25 },
        ],
      }}
      recent={{
        title: "Actividad reciente",
        items: [
          { primary: "Reserva confirmada — MEX→MIA aérea", secondary: "850 kg · CIF · 14:20", badge: "Confirmada", tone: "ok" },
          { primary: "Cotización LCL — Manzanillo→Shanghai", secondary: "50% 40'HC · FOB · 13:05", badge: "En revisión", tone: "warn" },
          { primary: "Reserva FCL — Veracruz→Rotterdam", secondary: "40' Reefer · DDP · 11:40", badge: "Reservado", tone: "info" },
          { primary: "Carga peligrosa — DG aérea", secondary: "120 kg · DAP · 10:15", badge: "Pendiente", tone: "warn" },
        ],
      }}
    />
  );
}

export function CotizadorAereoSection() {
  return <MultimodalQuoter />;
}

// --- Disponibilidad y tarifas (datos del proveedor — mock) -----------------
interface Disponibilidad extends Record<string, unknown> {
  id: string; modo: string; ruta: string; proveedor: string; salida: string; tarifa: string; espacio: string; estado: string;
}
const dispColumns: CrudColumn<Disponibilidad>[] = [
  { key: "modo", label: "Modo" },
  { key: "ruta", label: "Ruta" },
  { key: "proveedor", label: "Proveedor" },
  { key: "salida", label: "Salida" },
  { key: "tarifa", label: "Tarifa" },
  { key: "espacio", label: "Espacio" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const dispFields: CrudField[] = [
  { key: "modo", label: "Modo", type: "select", required: true, options: [
    { value: "Aérea", label: "Aérea" }, { value: "Marítima FCL", label: "Marítima FCL" },
    { value: "Marítima LCL", label: "Marítima LCL" }, { value: "Terrestre int.", label: "Terrestre int." },
  ] },
  { key: "ruta", label: "Ruta", required: true, placeholder: "MEX → MIA" },
  { key: "proveedor", label: "Proveedor", required: true, placeholder: "Aerolínea / naviera" },
  { key: "salida", label: "Salida", type: "date", required: true },
  { key: "tarifa", label: "Tarifa", required: true, placeholder: "$ 3.80 / kg" },
  { key: "espacio", label: "Espacio", placeholder: "1,200 kg / 3 TEU" },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Disponible", options: [
    { value: "Disponible", label: "Disponible" }, { value: "Parcial", label: "Parcial" }, { value: "Agotado", label: "Agotado" },
  ] },
];
const dispSeed: Disponibilidad[] = [
  { id: "d1", modo: "Aérea", ruta: "MEX → MIA", proveedor: "AeroCargo", salida: "2026-06-18", tarifa: "$ 3.80 / kg", espacio: "1,200 kg", estado: "Disponible" },
  { id: "d2", modo: "Marítima FCL", ruta: "Manzanillo → Shanghai", proveedor: "OceanLine", salida: "2026-06-22", tarifa: "$ 32,400 / 40'HC", espacio: "8 TEU", estado: "Parcial" },
  { id: "d3", modo: "Marítima LCL", ruta: "Veracruz → Rotterdam", proveedor: "EuroFreight", salida: "2026-06-25", tarifa: "$ 95 / m³", espacio: "40 m³", estado: "Disponible" },
  { id: "d4", modo: "Aérea", ruta: "GDL → LAX", proveedor: "SkyFreight", salida: "2026-06-19", tarifa: "$ 4.10 / kg", espacio: "Agotado", estado: "Agotado" },
];
export const DisponibilidadSection = () => (
  <CrudSection<Disponibilidad> title="Disponibilidad y tarifas" subtitle="Espacio de carga del proveedor (datos de ejemplo)"
    icon="ri-search-eye-line" columns={dispColumns} fields={dispFields} seed={dispSeed} searchKey="ruta" />
);

// --- Reservas / booking (compra end-to-end — store en vivo) ----------------
// Lists the real booking transactions registered by the checkout (quoter →
// BookingCheckout → bookingService). Read-only: a booking is created by buying
// space, not by hand. Each row opens a detail modal with its issued documents
// (booking ref, AWB/BL, factura) and the state-transition log.

const STATUS_LABEL: Record<BookingStatus, string> = {
  reserving: "Reservando",
  reserved: "Reservada",
  payment_pending: "Pendiente",
  paid: "Pagado",
  confirmed: "Confirmada",
  failed: "Fallida",
  cancelled: "Cancelada",
};
const PAYMENT_LABEL: Record<PaymentStatus, string> = {
  pending: "Pendiente",
  authorized: "Autorizado",
  paid: "Pagado",
  declined: "Rechazado",
  refunded: "Reembolsado",
};

type ReservaRow = CrudRow & {
  booking: CargoBooking;
  folio: string; ruta: string; modo: string; incoterm: string; total: string; pago: string; estado: string;
};

const reservaColumns: CrudColumn<ReservaRow>[] = [
  {
    key: "folio",
    label: "Folio",
    render: (r) => (
      <span className="inline-flex items-center gap-2">
        {r.folio}
        {r.booking.sandbox && <StatusTextBadge value="Ejemplo" />}
      </span>
    ),
  },
  { key: "ruta", label: "Ruta" },
  { key: "modo", label: "Modo" },
  { key: "incoterm", label: "Incoterm" },
  { key: "total", label: "Total", align: "right" },
  { key: "pago", label: "Pago", align: "center", render: (r) => <StatusTextBadge value={r.pago} /> },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];

export const ReservasSection = () => {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const bookings = useBookings();
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState<CargoBooking | null>(null);

  const rows: ReservaRow[] = useMemo(
    () =>
      bookings.map((b) => ({
        id: b.id,
        folio: b.folio,
        ruta: b.route,
        modo: b.mode,
        incoterm: b.incoterm,
        total: formatMoney(b.amount, b.currency),
        pago: PAYMENT_LABEL[b.paymentStatus] ?? b.paymentStatus,
        estado: STATUS_LABEL[b.status] ?? b.status,
        booking: b,
      })),
    [bookings],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter(
      (r) => r.folio.toLowerCase().includes(q) || r.ruta.toLowerCase().includes(q),
    );
  }, [rows, query]);

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-shopping-cart-2-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Reservas y booking</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Compra de espacio end-to-end (booking + pago) · transacciones registradas
              </p>
            </div>
          </div>
          <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
            <i className={`ri-search-line ${tc.textSecondary}`} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar folio o ruta..."
              className={`bg-transparent text-sm outline-none ${tc.text}`}
            />
          </div>
        </div>

        <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
          <DataTable<ReservaRow>
            rows={filtered}
            columns={reservaColumns}
            emptyText="Aún no hay reservas. Cotiza y compra espacio en el Cotizador multimodal."
            actionsLabel="Detalle"
            renderActions={(r) => (
              <button
                title="Ver detalle"
                onClick={() => setDetail(r.booking)}
                className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
              >
                <i className="ri-file-list-3-line" />
              </button>
            )}
          />
        </div>

        <p className={`mt-3 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          Las reservas se crean al comprar espacio desde el cotizador y se registran en
          Firestore (colección <code>cargo_bookings</code>) cuando el backend está disponible.
        </p>
      </div>

      {detail && (
        <Modal
          title={`Reserva ${detail.folio}`}
          onClose={() => setDetail(null)}
          themeClasses={tc}
          width="max-w-lg"
          content={<BookingDetail booking={detail} />}
        />
      )}
    </div>
  );
};

function BookingDetail({ booking }: { booking: CargoBooking }) {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const { t: td } = useTranslation(DOCS_NS);
  // Pestañas (CC-CONSOLE #1632): la reserva y su expediente documental.
  const [tab, setTab] = useState<"booking" | "documents">("booking");
  return (
    <div className="space-y-4">
      <Tabs
        items={[
          { id: "booking", label: td("tabs.booking"), icon: "ri-ship-2-line" },
          { id: "documents", label: td("tabs.documents"), icon: "ri-folder-shield-2-line" },
        ]}
        activeId={tab}
        onChange={(id) => setTab(id as "booking" | "documents")}
      />
      {tab === "documents" ? (
        <div data-testid="booking-documents-tab">
          <DocumentChecklist subject={{ kind: "booking", id: booking.id }} title={booking.folio} compact />
        </div>
      ) : (
        <BookingSummary booking={booking} tc={tc} />
      )}
    </div>
  );
}

function BookingSummary({ booking, tc }: { booking: CargoBooking; tc: ReturnType<typeof buildThemeClasses> }) {
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        <div className="flex justify-between"><dt className={tc.textSecondary}>Ruta</dt><dd className={tc.text}>{booking.route}</dd></div>
        <div className="flex justify-between"><dt className={tc.textSecondary}>Modo</dt><dd className={tc.text}>{booking.mode}</dd></div>
        <div className="flex justify-between"><dt className={tc.textSecondary}>Incoterm</dt><dd className={tc.text}>{booking.incoterm}</dd></div>
        <div className="flex justify-between"><dt className={tc.textSecondary}>Proveedor</dt><dd className={tc.text}>{booking.provider || "—"}</dd></div>
        <div className="flex justify-between"><dt className={tc.textSecondary}>Total</dt><dd className={tc.text}>{formatMoney(booking.amount, booking.currency)}</dd></div>
        <div className="flex justify-between"><dt className={tc.textSecondary}>Pago</dt><dd><StatusTextBadge value={PAYMENT_LABEL[booking.paymentStatus] ?? booking.paymentStatus} /></dd></div>
      </dl>

      <div>
        <h4 className={`mb-1.5 text-xs font-semibold ${tc.text}`}>Documentos</h4>
        {booking.documents.length === 0 ? (
          <p className={`text-xs ${tc.textSecondary}`}>Sin documentos emitidos.</p>
        ) : (
          <ul className="space-y-1">
            {booking.documents.map((d) => (
              <li key={d.type} className="flex items-center justify-between text-xs">
                <span className={tc.textSecondary}>{d.label}</span>
                <span className={`font-mono ${tc.text}`}>{d.reference}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {booking.events.length > 0 && (
        <div>
          <h4 className={`mb-1.5 text-xs font-semibold ${tc.text}`}>Historial de estados</h4>
          <ol className="space-y-1.5">
            {booking.events.map((e, i) => (
              <li key={i} className="flex items-start gap-2 text-xs">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />
                <span className={tc.textSecondary}>
                  <b className={tc.text}>{STATUS_LABEL[e.status] ?? e.status}</b> — {e.message}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
